import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Paper,
  Typography,
  Button,
  Chip,
  Collapse,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@material-ui/core'
import {
  ExpandMore,
  ExpandLess,
  Check as ConfirmIcon,
  Close as RejectIcon,
  CompareArrows
} from '@material-ui/icons'

const scoreColor = (score) => {
  if (score >= 0.7) return '#4caf50'
  if (score >= 0.5) return '#ff9800'
  return '#9e9e9e'
}

const MatchSuggestionsPanel = ({ projectId, url, onMatchConfirmed }) => {
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [collapsed, setCollapsed] = useState(false)
  const [confirming, setConfirming] = useState(new Set())

  useEffect(() => {
    if (!projectId || !url) return
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/line-items/match-suggestions`)
      .then(res => {
        setMatches(res.data.matches || [])
        setLoading(false)
      })
      .catch(() => {
        setMatches([])
        setLoading(false)
      })
  }, [projectId, url])

  const handleConfirm = async (brLineItemId, planLineItemId) => {
    setConfirming(prev => new Set([...prev, brLineItemId]))
    try {
      await axios.post(`${url}/api/bid-estimation/line-items/${brLineItemId}/confirm-match`, {
        planLineItemIds: [planLineItemId],
        action: 'confirm'
      })
      setMatches(prev => prev.filter(m => m.bidResponseLineItemId !== brLineItemId))
      if (onMatchConfirmed) onMatchConfirmed()
    } catch (err) {
      console.error('Confirm match failed:', err)
    } finally {
      setConfirming(prev => {
        const next = new Set(prev)
        next.delete(brLineItemId)
        return next
      })
    }
  }

  const handleReject = async (brLineItemId) => {
    setConfirming(prev => new Set([...prev, brLineItemId]))
    try {
      await axios.post(`${url}/api/bid-estimation/line-items/${brLineItemId}/confirm-match`, {
        action: 'reject'
      })
      setMatches(prev => prev.filter(m => m.bidResponseLineItemId !== brLineItemId))
    } catch (err) {
      console.error('Reject match failed:', err)
    } finally {
      setConfirming(prev => {
        const next = new Set(prev)
        next.delete(brLineItemId)
        return next
      })
    }
  }

  // Don't render anything if no matches
  if (!loading && matches.length === 0) return null

  return (
    <Paper style={{ padding: 16, marginBottom: 16, border: '1px solid #bbdefb', backgroundColor: '#e3f2fd' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CompareArrows style={{ color: '#1565c0' }} />
          <Typography variant="subtitle2" style={{ color: '#1565c0' }}>
            Match Suggestions
          </Typography>
          <Chip label={loading ? '...' : matches.length} size="small"
            style={{ backgroundColor: '#1565c0', color: '#fff', height: 22 }} />
        </div>
        <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </div>

      <Collapse in={!collapsed}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
              Plan items that may match bid response items. Confirm to link them or reject to dismiss.
            </Typography>
            <Table size="small" style={{ backgroundColor: '#fff', borderRadius: 4 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Bid Response Item</TableCell>
                  <TableCell>Best Plan Match</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Reasons</TableCell>
                  <TableCell align="center" style={{ width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matches.map(match => {
                  const best = match.suggestions[0]
                  if (!best) return null
                  const isProcessing = confirming.has(match.bidResponseLineItemId)

                  return (
                    <TableRow key={match.bidResponseLineItemId} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {match.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {match.quantity} {match.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {best.description}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {best.quantity} {best.unit}
                          {match.suggestions.length > 1 && (
                            <span style={{ marginLeft: 8, color: '#1565c0' }}>
                              +{match.suggestions.length - 1} more
                            </span>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${Math.round(best.score * 100)}%`}
                          size="small"
                          style={{
                            backgroundColor: scoreColor(best.score),
                            color: '#fff',
                            fontWeight: 600,
                            height: 22
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {best.reasons.map(r => (
                          <Chip key={r} label={r} size="small"
                            variant="outlined" style={{ margin: 1, height: 20, fontSize: 11 }} />
                        ))}
                      </TableCell>
                      <TableCell align="center">
                        {isProcessing ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              title="Confirm match"
                              style={{ color: '#4caf50' }}
                              onClick={() => handleConfirm(match.bidResponseLineItemId, best.planLineItemId)}
                            >
                              <ConfirmIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Reject suggestion"
                              style={{ color: '#f44336' }}
                              onClick={() => handleReject(match.bidResponseLineItemId)}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Collapse>
    </Paper>
  )
}

export default MatchSuggestionsPanel
